import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import styles from "./Login.module.css";
import LogoImg from "../../assets/sign-in-logo.png";
import Nav from "../../components/Nav/Nav";
import AirlineDropdown from "../../components/AirlineDropdown";
import Footer from "../../components/Footer/Footer";
import { StaticRoutes } from "../routes";
import { AccountContainer } from "../../hooks/useAccount";
import { Auth } from "../../auth";
import Loader from "../../components/Loader/Loader";
import environment from "../../environment";
import { Link } from "react-router-dom";

const Login = ({
  navList = [
    {
      label: "HOME",
      options: [],
      border: false,
      path: StaticRoutes.Home,
    },
    {
      label: "PRODUCTS",
      options: [
        { label: "BIDDING", path: StaticRoutes.Bidding },
        { label: "TRIPBOARD", path: StaticRoutes.TripBoard },
        { label: "SENIORITY", path: StaticRoutes.Seniority },
      ],
      border: false,
    },
    {
      label: "PRICING",
      options: [
        { label: "UPS", path: StaticRoutes.UPSPricing },
        { label: "AMERICAN", path: StaticRoutes.Bidding },
        { label: "ALASKA", path: StaticRoutes.TripBoard },
        { label: "JETSUITE", path: StaticRoutes.Bidding },
      ],
      border: false,
    },
    {
      label: "CONTACT US",
      options: [],
      border: false,
      path: StaticRoutes.Contact,
    },
    // {
    //   label: "LOG IN",
    //   options: [],
    //   border: false,
    //   path: StaticRoutes.Login,
    // },
    {
      label: "SIGN UP",
      options: [
        { label: "UPS", path: StaticRoutes.UPSPricing },
        { label: "AMERICAN", path: StaticRoutes.Bidding },
        { label: "ALASKA", path: StaticRoutes.TripBoard },
        { label: "JETSUITE", path: StaticRoutes.Bidding },
      ],
      border: true,
    },
  ],
  footerLists = [
    {
      header: "MENU",
      links: [
        { name: "Home", src: StaticRoutes.Home },
        { name: "Contact Us", src: StaticRoutes.Contact },
        { name: "Sign Up", src: StaticRoutes.Home },
      ],
    },
    {
      header: "PRODUCTS",
      links: [
        { name: "Bidding", src: StaticRoutes.Bidding },
        { name: "Trip Board", src: StaticRoutes.TripBoard },
        { name: "Seniority", src: StaticRoutes.Seniority },
        { name: "Pricing", src: StaticRoutes.UPSPricing },
      ],
    },
    {
      header: "TECH SUPPORT",
      links: [
        { name: "tech@bidpro.com", src: "mailto:tech@bidpro.com" },
        { name: "1-800-918-4980", src: "tel:18009184980" },
      ],
    },
  ],
}) => {
  const { login } = AccountContainer.useContainer();
  const { isLoggedIn } = Auth.useContainer();
  const navigate = useNavigate();
  const airlines = [
    { label: "Select Airline" },
    { label: "UPS" },
    { label: "Alaska Airlines" },
    { label: "American Airlines" },
  ];
  const placeholder = "Select Airline";
  const [animate, setAnimate] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showNewToBidPro, setShowNewToBidPro] = useState(false);
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [selectedAirline, setSelectedAirline] = useState(placeholder);
  const [touched, setTouched] = useState({
    employeeNumber: false,
    password: false,
    airline: false,
    email: false,
  });
  const [airlineErrorMessage, setAirlineErrorMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [passwordForgotError, setPasswordForgotError] = useState(true);

  useEffect(() => {
    if (isLoggedIn) {
      //user already logged in. return to home.
      navigate(StaticRoutes.Home);
    }

    const handleAnimation = () => {
      setAnimate(true);
      setTimeout(() => setAnimate(false), 1000);
    };

    handleAnimation();
  }, []);

  const navListWithNavigate = navList.map((item) => ({
    ...item,
    onClick: () => navigate(item.path),
    options: item.options?.map((option) => ({
      ...option,
      onClick: () => navigate(option.path),
    })),
  }));

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "employeeNumber") {
      setEmployeeNumber(value);
    } else if (name === "password") {
      setPassword(value);
    } else if (name === "email") {
      setEmail(value);
    }

    setLoginError(false);
    setPasswordForgotError(false);
  };

  const handleAirlineChange = (e) => {
    setSelectedAirline(e.target.value);
    setTouched({ ...touched, airline: true });
    setAirlineErrorMessage("");
    setErrors({});
    setLoginError(false);
    setPasswordForgotError(false);
  };

  const validateLogin = () => {
    let airlineError = false;

    if (selectedAirline === placeholder || selectedAirline !== "UPS") {
      //currently only letting UPS logins
      airlineError = true;

      setAirlineErrorMessage("Only UPS logins are allowed at this time.");
    }

    return {
      employeeNumber: employeeNumber.length === 0,
      password: password.length === 0,
      airline: airlineError,
    };
  };

  const validateEmail = () => {
    //hate regex but whatever
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    let airlineError = false;

    if (selectedAirline === placeholder || selectedAirline !== "UPS") {
      //currently only letting UPS logins
      airlineError = true;

      setAirlineErrorMessage("Only UPS logins are allowed at this time.");
    }

    return {
      airline: airlineError,
      email: email.length === 0 || !emailRegex.test(email),
    };
  };

  const shouldMarkError = (field) => {
    return touched[field] && errors[field];
  };

  const handleLoginSubmit = async (e) => {
    //ensure before anything we clear the values on sub screens
    //helps browser identify what is happening (do you want to save this new password? thing that browsers like to do)
    setEmail("");

    setIsLoading(true);
    setErrors(validateLogin());
    let formErrors = validateLogin();

    e.preventDefault();
    if (
      !formErrors.employeeNumber &&
      !formErrors.password &&
      !formErrors.airline
    ) {
      // Submit form
      try {
        await login({ employeeNumber: employeeNumber, password });
        setIsLoading(false);
        window.location.href = "/";
      } catch (error) {
        setLoginError(true);
        setIsLoading(false);
      }
    } else {
      setTouched({ employeeNumber: true, password: true, airline: true });
      setIsLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    //ensure before anything we clear the values on sub screens
    //helps browser identify what is happening (do you want to save this new password? thing that browsers like to do)
    setEmployeeNumber("");
    setPassword("");

    setIsLoading(true);
    setErrors(validateEmail());
    let formErrors = validateEmail();

    e.preventDefault();
    if (!formErrors.email && !formErrors.airline) {
      //Submit Password Forgot Form
      try {
        let resetRes = await passwordResetPost(email);

        if (resetRes.success) {
          setIsLoading(false);
          navigate(StaticRoutes.PasswordResetSent);
        } else {
          throw new Error();
        }
      } catch (error) {
        setPasswordForgotError(true);
        setIsLoading(false);
      }
    } else {
      setTouched({ email: true, airline: true });
      setIsLoading(false);
    }
  };

  const handleForgotPasswordClick = () => {
    setPasswordForgotError(false);
    setLoginError(false);
    setShowForgotPassword(true);
    setErrors({});
  };

  const handleGoBackClick = () => {
    setShowForgotPassword(false);
    setShowNewToBidPro(false);
    setErrors({});
  };

  const handleNewToBidProClick = () => {
    setPasswordForgotError(false);
    setLoginError(false);
    setShowNewToBidPro(true);
    setErrors({});
  };

  const passwordResetPost = async (emailPassed) => {
    let newBody = {
      email: emailPassed,
    };

    try {
      const response = await fetch(
        environment.apiBase + "/account/password-reset-request",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(newBody),
          cache: "no-store",
        }
      );

      if (response.status == 200) {
        return { success: true };
      } else {
        return {
          success: false,
        };
      }
    } catch (error) {
      return {
        success: false,
      };
    }
  };

  return (
    <>
      <Nav isStatic={true} navItems={navListWithNavigate} />
      <div className={styles.pageContainer}>
        <div className={styles.background}>
          {!showForgotPassword && !showNewToBidPro ? (
            <div
              className={`${styles.loginBox} ${animate ? styles.slideIn : ""}`}
            >
              <img src={LogoImg} id={styles.logo} alt="BidPro Logo" />
              <h3 className={styles.h3}>Login</h3>
              <form
                className="needs-validation"
                id="loginForm"
                onSubmit={handleLoginSubmit}
              >
                <div className="mb-3">
                  <input
                    type="text"
                    name="employeeNumber"
                    className={`form-control ${
                      shouldMarkError("employeeNumber") ? styles.invalid : ""
                    } `}
                    placeholder="Employee Number"
                    value={employeeNumber}
                    onChange={handleChange}
                    onBlur={() => handleBlur("employeeNumber")}
                  />
                  {shouldMarkError("employeeNumber") && (
                    <div className={styles.errorText}>
                      Please enter username.
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <input
                    type="password"
                    name="password"
                    className={`form-control ${
                      shouldMarkError("password") ? styles.invalid : ""
                    }`}
                    placeholder="Password"
                    value={password}
                    onChange={handleChange}
                    onBlur={() => handleBlur("password")}
                  />
                  {shouldMarkError("password") && (
                    <div className={styles.errorText}>
                      Please enter a password.
                    </div>
                  )}
                </div>
                <div>
                  <AirlineDropdown
                    airlines={airlines}
                    value={selectedAirline}
                    onChange={handleAirlineChange}
                    onBlur={() => handleBlur("airline")}
                    error={shouldMarkError("airline")}
                    errorMessage={airlineErrorMessage}
                  />
                </div>
                <div>
                  <button
                    className="btn"
                    id={styles.btn}
                    type="submit"
                    disabled={
                      !employeeNumber ||
                      !password ||
                      selectedAirline === placeholder ||
                      isLoading
                    }
                  >
                    {!isLoading ? (
                      "Login"
                    ) : (
                      <Loader color={"white"} size={70} />
                    )}
                  </button>
                </div>
              </form>
              <div className={styles.forgotPasswordContainer}>
                <button
                  className="btn btn-link"
                  id={styles.forgotPassword}
                  onClick={handleForgotPasswordClick}
                >
                  Forgot your password?
                </button>
              </div>
              <div className={styles.forgotPasswordContainer}>
                <button
                  className="btn btn-link"
                  id={styles.newToBidPro}
                  onClick={handleNewToBidProClick}
                >
                  New to BidPro?
                </button>
              </div>
              {loginError ? (
                <div className={styles.invalidMessage}>
                  <p>
                    Invalid username or password. Please contact support at
                    <Link to="mailto:tech@bidpro.com">
                      {" "}
                      tech@bidpro.com
                    </Link>{" "}
                    for assistance.
                  </p>
                </div>
              ) : null}
            </div>
          ) : !showNewToBidPro ? (
            <div
              className={`${styles.loginBox} ${animate ? styles.slideIn : ""}`}
            >
              <img src={LogoImg} id={styles.logo} alt="BidPro Logo" />
              <h3 className={styles.h3}>Forgot Password</h3>
              <form
                className="needs-validation"
                id="forgotPasswordForm"
                onSubmit={handleForgotPasswordSubmit}
              >
                <div className="mb-3">
                  <AirlineDropdown
                    airlines={airlines}
                    value={selectedAirline}
                    onChange={handleAirlineChange}
                    onBlur={() => handleBlur("airline")}
                    error={shouldMarkError("airline")}
                    errorMessage={airlineErrorMessage}
                  />
                </div>
                <div className="mb-3">
                  <input
                    type="email"
                    name="email"
                    className={`form-control ${
                      shouldMarkError("email") ? styles.invalid : ""
                    } `}
                    placeholder="Email"
                    value={email}
                    onChange={handleChange}
                    onBlur={() => handleBlur("email")}
                  />
                  {shouldMarkError("email") && (
                    <div className={styles.errorText}>
                      {email.length === 0
                        ? "Email is required"
                        : "Invalid email format"}
                    </div>
                  )}
                </div>
                <button
                  className="btn"
                  id={styles.btn}
                  type="submit"
                  disabled={
                    selectedAirline === placeholder ||
                    !email ||
                    errors.email ||
                    isLoading
                  }
                >
                  {!isLoading ? (
                    "Reset Password"
                  ) : (
                    <Loader color={"white"} size={70} />
                  )}
                </button>
              </form>
              <div className={styles.forgotPasswordContainer}>
                <button
                  className="btn btn-link"
                  id={styles.forgotPassword}
                  onClick={handleGoBackClick}
                >
                  Go back
                </button>
              </div>
              {passwordForgotError ? (
                <div className={styles.invalidMessage}>
                  <p>
                    Unable to send password reset. Please contact support at
                    <Link to="mailto:tech@bidpro.com">
                      {" "}
                      tech@bidpro.com
                    </Link>{" "}
                    for assistance.
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}

          {showNewToBidPro ? (
            <div className={styles.background}>
              <div
                className={`${styles.loginBox} ${
                  animate ? styles.slideIn : ""
                }`}
              >
                {/* <div> */}
                <img src={LogoImg} id={styles.logo} alt="BidPro Logo" />
                <h3 className={styles.h3}>New to BidPro?</h3>
                <div className={styles.newToBidProContent}>
                  <p>
                    If you are a not a new hire, or did not receive a welcome
                    email with login instructions and a temporary password,
                    please reach out to us at
                    <Link to="mailto:tech@bidpro.com"> tech@bidpro.com</Link>.
                  </p>
                </div>
                <div className={styles.forgotPasswordContainer}>
                  <button
                    className="btn btn-link"
                    id={styles.forgotPassword}
                    onClick={handleGoBackClick}
                  >
                    Go back
                  </button>
                </div>
                {/* </div> */}
              </div>
            </div>
          ) : (
            <div></div>
          )}
        </div>
      </div>
      <Footer lists={footerLists} inverse={true} className={styles.footer} />
    </>
  );
};

Login.propTypes = {
  navList: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          path: PropTypes.string.isRequired,
        })
      ),
      border: PropTypes.bool,
      path: PropTypes.string.isRequired,
    })
  ),
};

export default Login;
